import React, { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box, Typography, Stack, Chip, Button, Divider, Avatar,
} from '@mui/material'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { Navebarehero } from '../Components/Navebarehero'
import { blogsData, getBlogBySlug } from '../Data/blogsData'

const SITE_URL = 'https://lyorauraproperties.com'

export const BlogDetails = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { i18n } = useTranslation()
  const isArabic = (i18n.language || 'ar').startsWith('ar')
  const lang = isArabic ? 'ar' : 'en'

  const headingFont = isArabic ? "'El Messiri', serif" : "'Bodoni Moda', serif"
  const bodyFont = isArabic ? "'IBM Plex Sans Arabic', sans-serif" : "'Manrope', sans-serif"

  const blog = getBlogBySlug(slug)

  // Schema ديناميكي لكل مقال
  useEffect(() => {
    if (!blog) return

    const scriptId = 'blog-schema'
    document.getElementById(scriptId)?.remove()

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: blog.title[lang],
      description: blog.excerpt[lang],
      image: blog.image,
      datePublished: blog.date,
      dateModified: blog.date,
      author: {
        '@type': 'Organization',
        name: 'Lyor Aura',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Lyor Aura',
        logo: {
          '@type': 'ImageObject',
          url: `${SITE_URL}/logo.png`,
        },
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${SITE_URL}/blogs/${blog.slug}`,
      },
      articleSection: blog.category[lang],
      inLanguage: lang,
      wordCount: blog.content[lang].split(/\s+/).length,
    }

    const script = document.createElement('script')
    script.id = scriptId
    script.type = 'application/ld+json'
    script.textContent = JSON.stringify(schema)
    document.head.appendChild(script)

    // تحديث meta tags ديناميكيًا
    document.title = `${blog.title[lang]} | Lyor Aura`
    const setMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`)
      if (!el) {
        el = document.createElement('meta')
        el.setAttribute(attr, key)
        document.head.appendChild(el)
      }
      el.setAttribute('content', content)
    }
    setMeta('name', 'description', blog.excerpt[lang])
    setMeta('property', 'og:title', blog.title[lang])
    setMeta('property', 'og:description', blog.excerpt[lang])
    setMeta('property', 'og:image', blog.image)
    setMeta('property', 'og:url', `${SITE_URL}/blogs/${blog.slug}`)
    setMeta('property', 'og:type', 'article')
    setMeta('name', 'twitter:title', blog.title[lang])
    setMeta('name', 'twitter:description', blog.excerpt[lang])
    setMeta('name', 'twitter:image', blog.image)

    // canonical
    let canonical = document.querySelector('link[rel="canonical"]')
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.setAttribute('rel', 'canonical')
      document.head.appendChild(canonical)
    }
    canonical.setAttribute('href', `${SITE_URL}/blogs/${blog.slug}`)
  }, [blog, lang])

  if (!blog) {
    return (
      <Box sx={{ bgcolor: '#2B2B2B', minHeight: '100vh' }}>
        <Navebarehero />
        <Stack alignItems="center" justifyContent="center" sx={{ height: '70vh' }}>
          <Typography variant="h4" sx={{ color: '#D9D6CF' }}>
            {isArabic ? 'المقال غير موجود' : 'Article not found'}
          </Typography>
          <Button onClick={() => navigate('/blogs')} sx={{ mt: 2, color: '#7A2E3A' }}>
            {isArabic ? 'رجوع للمدونة' : 'Back to blog'}
          </Button>
        </Stack>
      </Box>
    )
  }

  // مقالات ذات صلة (نفس الكاتيجوري)
  const related = blogsData
    .filter((b) => b.slug !== blog.slug && b.category[lang] === blog.category[lang])
    .slice(0, 3)

  // ✨ بارسر للمحتوى - يدعم العناوين الفرعية والقوائم
  const renderContent = (text) => {
    const paragraphs = text.split('\n\n')
    return paragraphs.map((para, idx) => {
      // عنوان فرعي (**...**)
      if (para.startsWith('**') && para.endsWith('**')) {
        return (
          <Typography
            key={idx}
            component="h2"
            sx={{
              fontFamily: headingFont,
              fontSize: { xs: '1.3rem', md: '1.6rem' },
              fontWeight: 700,
              color: '#D9D6CF',
              mt: 4,
              mb: 2,
            }}
          >
            {para.replace(/\*\*/g, '')}
          </Typography>
        )
      }

      // قائمة نقطية (سطور تبدأ بـ -)
      if (para.includes('\n- ')) {
        const [firstLine, ...items] = para.split('\n- ')
        return (
          <Box key={idx} sx={{ mb: 3 }}>
            {firstLine && (
              <Typography
                sx={{
                  color: 'rgba(217,214,207,0.85)',
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  lineHeight: 2,
                  fontFamily: bodyFont,
                  mb: 1,
                }}
              >
                {firstLine}
              </Typography>
            )}
            <Box component="ul" sx={{ pl: 3, m: 0 }}>
              {items.map((item, i) => (
                <Typography
                  key={i}
                  component="li"
                  sx={{
                    color: 'rgba(217,214,207,0.85)',
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    lineHeight: 1.9,
                    fontFamily: bodyFont,
                    mb: 0.5,
                  }}
                >
                  {item}
                </Typography>
              ))}
            </Box>
          </Box>
        )
      }

      // فقرة عادية
      return (
        <Typography
          key={idx}
          sx={{
            color: 'rgba(217,214,207,0.85)',
            fontSize: { xs: '1rem', md: '1.1rem' },
            lineHeight: 2,
            fontFamily: bodyFont,
            mb: 3,
          }}
        >
          {para}
        </Typography>
      )
    })
  }

  return (
    <Box sx={{ bgcolor: '#2B2B2B', minHeight: '100vh' }}>
      <Navebarehero />

      {/* Hero */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: { xs: '60vh', md: '85vh' },
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src={blog.image}
          alt={blog.title[lang]}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />

        {/* overlay أساسي لتحسين تباين النص */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            background: 'linear-gradient(180deg, rgba(43,43,43,0.55) 0%, rgba(43,43,43,0.15) 40%, rgba(43,43,43,0.55) 100%)',
          }}
        />

        {/* ✨ gradient ممتد لأسفل الصورة عشان يمتزج مع خلفية الصفحة */}
        <Box
          sx={{
            position: 'absolute',
            bottom: -1,
            left: 0,
            right: 0,
            height: { xs: '40%', md: '50%' },
            background:
              'linear-gradient(180deg, rgba(43,43,43,0) 0%, rgba(43,43,43,0.35) 35%, rgba(43,43,43,0.8) 70%, #2B2B2B 100%)',
            pointerEvents: 'none',
            zIndex: 2,
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            px: { xs: 3, md: 8 },
            pb: { xs: 4, md: 6 },
            zIndex: 3,
          }}
        >
          <Button
            onClick={() => navigate('/blogs')}
            startIcon={<ArrowBackIcon sx={{ transform: isArabic ? 'scaleX(-1)' : 'none' }} />}
            sx={{
              alignSelf: 'flex-start',
              mb: 2,
              color: '#D9D6CF',
              border: '1px solid rgba(217,214,207,0.3)',
              borderRadius: '999px',
              px: 2,
              '&:hover': { borderColor: '#7A2E3A', bgcolor: 'rgba(122,46,58,0.1)' },
            }}
          >
            {isArabic ? 'رجوع' : 'Back'}
          </Button>

          <Chip
            label={blog.category[lang]}
            sx={{ alignSelf: 'flex-start', mb: 1.5, bgcolor: 'rgba(122,46,58,0.9)', color: '#fff' }}
          />

          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontFamily: headingFont,
              fontWeight: isArabic ? 700 : 600,
              fontSize: { xs: '1.6rem', md: '3rem' },
              color: '#fff',
              lineHeight: 1.3,
              maxWidth: 900,
            }}
          >
            {blog.title[lang]}
          </Typography>

          <Stack direction="row" spacing={3} alignItems="center" sx={{ mt: 2, flexWrap: 'wrap' }}>
            <Stack direction="row" spacing={0.7} alignItems="center">
              <Avatar sx={{ width: 28, height: 28, bgcolor: '#7A2E3A', fontSize: '0.8rem' }}>
                {blog.author[lang].charAt(0)}
              </Avatar>
              <Typography sx={{ color: 'rgba(217,214,207,0.85)', fontSize: '0.85rem', fontFamily: bodyFont }}>
                {blog.author[lang]}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <CalendarMonthIcon sx={{ fontSize: 16, color: 'rgba(217,214,207,0.6)' }} />
              <Typography sx={{ color: 'rgba(217,214,207,0.7)', fontSize: '0.85rem', fontFamily: bodyFont }}>
                {blog.dateDisplay?.[lang] || new Date(blog.date).toLocaleDateString(isArabic ? 'ar-AE' : 'en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <AccessTimeIcon sx={{ fontSize: 16, color: 'rgba(217,214,207,0.6)' }} />
              <Typography sx={{ color: 'rgba(217,214,207,0.7)', fontSize: '0.85rem', fontFamily: bodyFont }}>
                {blog.readTime} {isArabic ? 'دقائق قراءة' : 'min read'}
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Box>

      {/* المحتوى */}
      <Box
        component={motion.article}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        sx={{
          maxWidth: 800,
          mx: 'auto',
          px: { xs: 3, md: 4 },
          py: { xs: 6, md: 8 },
        }}
      >
        {renderContent(blog.content[lang])}

        <Divider sx={{ my: 6, borderColor: 'rgba(217,214,207,0.15)' }} />

        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar sx={{ width: 56, height: 56, bgcolor: '#7A2E3A', fontSize: '1.2rem' }}>
            {blog.author[lang].charAt(0)}
          </Avatar>
          <Box>
            <Typography sx={{ color: 'rgba(217,214,207,0.6)', fontSize: '0.8rem', fontFamily: bodyFont }}>
              {isArabic ? 'الكاتب' : 'Author'}
            </Typography>
            <Typography sx={{ color: '#D9D6CF', fontWeight: 600, fontSize: '1.1rem', fontFamily: headingFont }}>
              {blog.author[lang]}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* مقالات ذات صلة */}
      {related.length > 0 && (
        <Box sx={{ maxWidth: 1300, mx: 'auto', px: { xs: 3, md: 6 }, pb: { xs: 8, md: 12 } }}>
          <Typography
            sx={{
              fontFamily: headingFont,
              fontSize: { xs: '1.5rem', md: '2rem' },
              color: '#D9D6CF',
              mb: 4,
              textAlign: 'center',
            }}
          >
            {isArabic ? 'مقالات ذات صلة' : 'Related Articles'}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 3,
            }}
          >
            {related.map((item) => (
              <Box
                key={item.id}
                onClick={() => navigate(`/blogs/${item.slug}`)}
                sx={{
                  cursor: 'pointer',
                  borderRadius: '20px',
                  overflow: 'hidden',
                  border: '1px solid rgba(217,214,207,0.12)',
                  bgcolor: 'rgba(217,214,207,0.02)',
                  transition: 'border-color 0.3s, transform 0.3s',
                  '&:hover': {
                    borderColor: 'rgba(122,46,58,0.5)',
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <Box sx={{ position: 'relative', aspectRatio: '16 / 10', overflow: 'hidden' }}>
                  <Box
                    component="img"
                    src={item.image}
                    alt={item.title[lang]}
                    loading="lazy"
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Box>
                <Stack spacing={1} sx={{ p: 3 }}>
                  <Typography
                    sx={{
                      fontFamily: headingFont,
                      fontWeight: 600,
                      fontSize: '1rem',
                      color: '#D9D6CF',
                      lineHeight: 1.4,
                    }}
                  >
                    {item.title[lang]}
                  </Typography>
                  <Typography sx={{ color: 'rgba(217,214,207,0.5)', fontSize: '0.75rem', fontFamily: bodyFont }}>
                    {item.dateDisplay?.[lang] || new Date(item.date).toLocaleDateString(isArabic ? 'ar-AE' : 'en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </Typography>
                  <Button
                    endIcon={<ArrowForwardIcon sx={{ fontSize: 14, transform: isArabic ? 'scaleX(-1)' : 'none' }} />}
                    sx={{ alignSelf: 'flex-start', color: '#7A2E3A', p: 0, mt: 1, '&:hover': { bgcolor: 'transparent' } }}
                  >
                    {isArabic ? 'اقرأ المزيد' : 'Read More'}
                  </Button>
                </Stack>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  )
}